import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchPageContent } from '../lib/cms';
import useMeta from '../hooks/useMeta';

export default function Forside() {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      const data = await fetchPageContent('home');
      setPage(data);
      setLoading(false);
    }
    loadData();
  }, []);

  useMeta(page?.meta_title, page?.meta_description);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <i className="fa-light fa-spinner-third animate-spin text-3xl text-gold-500" />
          <p className="text-sm font-medium text-slate-500">Henter indhold...</p>
        </div>
      </div>
    );
  }

  // Optimize hero image URL with Cloudflare on-the-fly resizing
  const heroImage = page.images?.[0]
    ? `https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/721a44a9-57f8-4fae-acca-66fa85e14172/shutterstock-2391034671_1.jpg`
    : null;

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-6 md:px-12 py-20 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 flex flex-col items-start gap-6">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold-500">
                Privat Investeringsvirksomhed · Danmark
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium font-display tracking-tight text-navy-950 leading-[1.15]">
                {page.hero_title}
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-2xl">
                {page.hero_text}
              </p>
              <div className="flex flex-wrap gap-4 mt-4">
                <Link
                  to="/kontakt"
                  className="px-8 py-4 bg-gold-500 text-white text-xs font-bold uppercase tracking-[0.2em] rounded-none hover:bg-navy-950 hover:-translate-y-0.5 active:translate-y-0 shadow-sm hover:shadow-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gold-400"
                >
                  Kontakt os
                </Link>
                <Link
                  to="/om-os"
                  className="px-8 py-4 border border-navy-950 text-navy-950 text-xs font-bold uppercase tracking-[0.2em] rounded-none hover:bg-navy-950 hover:text-white transition-all duration-300 focus:outline-none focus:ring-1 focus:ring-navy-950"
                >
                  Læs om os
                </Link>
              </div>
            </div>

            {/* Right Image */}
            <div className="lg:col-span-5 relative">
              <div className="aspect-[4/5] lg:aspect-[3/4] overflow-hidden border border-slate-100 bg-slate-50 shadow-sm">
                {heroImage && (
                  <img
                    src={heroImage}
                    alt="KnowHow GROUP Hovedkontor"
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              {/* Subtle design element */}
              <div className="absolute -bottom-6 -left-6 w-24 h-24 border-l border-b border-gold-300/40 pointer-events-none hidden sm:block" />
            </div>
          </div>
        </div>
      </section>

      {/* Om KnowHow GROUP Section */}
      <section className="bg-slate-50 py-20 md:py-28 border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Left Column: Subtle Quote/Highlight */}
            <div className="lg:col-span-5 flex flex-col justify-between border-l-2 border-gold-500 pl-6 py-2">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold-500 block mb-4">
                  Specialiseret viden. Langsigtet kapital.
                </span>
                <h2 className="text-3xl font-medium font-display text-navy-950 mb-6">
                  Om KnowHow GROUP
                </h2>
                <p className="font-display text-xl italic text-navy-950 leading-relaxed">
                  "Vi leder ikke efter den næste trend. Vi søger de aktiver, som samfundet ikke kan undvære — og investerer i dem, mens verden stadig er ved at opdage dem."
                </p>
              </div>
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-8 block">
                — KnowHow GROUP · Filosofi
              </span>
            </div>

            {/* Right Column: Main Content */}
            <div className="lg:col-span-7 flex flex-col gap-6 text-slate-600 leading-relaxed text-base">
              <p className="font-medium text-navy-950 text-lg">
                KnowHow GROUP er en privat investeringsvirksomhed med fokus på de sektorer, der bærer den moderne verden — mobil infrastruktur og energi.
              </p>
              <p>
                Vi søger ikke kortsigtede gevinster. Vi investerer i den fysiske rygrad, som digitalisering og energiomstilling bygger på.
              </p>
              <p>
                Vores team besidder dybt sektorkendskab og et netværk, der åbner døre, som ellers forbliver lukkede. Vi investerer egne midler og giver udvalgte medejere adgang til stærke, veldokumenterede projekter.
              </p>
              <p>
                KnowHow GROUP er ikke en fond, og vi er ikke en bank. Vi er investorer med specialiseret viden, langsigtede ambitioner og kapital, vi er villige til at sætte bag vores overbevisninger.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sektorfokus Section */}
      <section className="py-20 md:py-28 bg-white border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold-500 block mb-3">
              To sektorer. Uundværlig infrastruktur.
            </span>
            <h2 className="text-3xl md:text-4xl font-medium font-display text-navy-950">
              Sektorfokus
            </h2>
          </div>

          {/* Sektorfokus Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {/* Sector 1 */}
            <div className="border border-slate-100 bg-slate-50/30 p-8 md:p-10 hover:border-gold-300/60 hover:bg-white hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between items-start">
              <div>
                <div className="flex justify-between items-center w-full mb-6">
                  <div className="w-12 h-12 bg-gold-50/50 border border-gold-100/60 flex items-center justify-center text-gold-500 text-lg">
                    <i className="fa-light fa-tower-cell" aria-hidden="true" />
                  </div>
                  <span className="text-4xl font-display font-light text-gold-200/50">01</span>
                </div>
                <h3 className="font-display text-xl font-medium text-navy-950 mb-4">
                  Mobil infrastruktur
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-6">
                  Vi investerer i tårne, master og transmissionsanlæg, der driver den næste generation af konnektivitet. Efterspørgslen på digital infrastruktur vokser eksponentielt — KnowHow GROUP er positioneret til at drage fordel af denne strukturelle megatrend.
                </p>
              </div>
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-gold-600 bg-gold-50/60 border border-gold-100/40 px-3 py-1.5">
                5G · Tårne · Transmission
              </span>
            </div>

            {/* Sector 2 */}
            <div className="border border-slate-100 bg-slate-50/30 p-8 md:p-10 hover:border-gold-300/60 hover:bg-white hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between items-start">
              <div>
                <div className="flex justify-between items-center w-full mb-6">
                  <div className="w-12 h-12 bg-gold-50/50 border border-gold-100/60 flex items-center justify-center text-gold-500 text-lg">
                    <i className="fa-light fa-bolt" aria-hidden="true" />
                  </div>
                  <span className="text-4xl font-display font-light text-gold-200/50">02</span>
                </div>
                <h3 className="font-display text-xl font-medium text-navy-950 mb-4">
                  Energibranchen
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-6">
                  Vores energiafdeling omfatter rådgivning inden for vedvarende energi, energilagring, strategisk indkøb og el-infrastruktur. Vi prioriterer aktiver med stabile, forudsigelige cash flows og langsigtede kontrakter — essentiel infrastruktur uanset konjunktur.
                </p>
              </div>
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-gold-600 bg-gold-50/60 border border-gold-100/40 px-3 py-1.5">
                Vedvarende energi · Stabile cash flows
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Investormodellen Section */}
      <section className="py-20 md:py-28 bg-slate-50 border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start mb-16">
            <div className="lg:col-span-5">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold-500 block mb-3">
                Skin in the game som fundament
              </span>
              <h2 className="text-3xl md:text-4xl font-medium font-display text-navy-950">
                Investormodellen
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-slate-600 leading-relaxed text-base">
                Hos KnowHow GROUP er skin in the game ikke bare et udtryk — det er fundamentet for alt, hvad vi gør. Vi investerer altid egne penge i de projekter, vi præsenterer for andre. Det skaber et naturligt interessesammenfald og den form for tillid, som langsigtede relationer bygger på.
              </p>
            </div>
          </div>

          {/* Pillars Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Pillar I */}
            <div className="bg-white p-8 md:p-10 border border-slate-100/80 hover:border-gold-300/40 hover:shadow-md hover:-translate-y-1 transition-all duration-300 flex flex-col items-start">
              <span className="text-3xl font-display font-light text-gold-500 mb-6 block tracking-widest">I</span>
              <h3 className="font-display text-lg font-medium text-navy-950 mb-4">
                Egne midler som anker
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                KnowHow GROUP investerer altid egenkapital som første kapital i hvert projekt. Intet projekt præsenteres for medejere, som vi ikke selv er med i.
              </p>
            </div>

            {/* Pillar II */}
            <div className="bg-white p-8 md:p-10 border border-slate-100/80 hover:border-gold-300/40 hover:shadow-md hover:-translate-y-1 transition-all duration-300 flex flex-col items-start">
              <span className="text-3xl font-display font-light text-gold-500 mb-6 block tracking-widest">II</span>
              <h3 className="font-display text-lg font-medium text-navy-950 mb-4">
                Plads til medejere
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Udvalgte projekter åbner vi for medejerskab — typisk i en struktureret, overskuelig form, der giver reel eksponering uden unødig kompleksitet.
              </p>
            </div>

            {/* Pillar III */}
            <div className="bg-white p-8 md:p-10 border border-slate-100/80 hover:border-gold-300/40 hover:shadow-md hover:-translate-y-1 transition-all duration-300 flex flex-col items-start">
              <span className="text-3xl font-display font-light text-gold-500 mb-6 block tracking-widest">III</span>
              <h3 className="font-display text-lg font-medium text-navy-950 mb-4">
                Fokus på stabile aktiver
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Vi prioriterer projekter med forudsigelige afkastprofiler og langsigtede kontrakter — infrastruktur, der er essentiel uanset konjunktur.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="bg-navy-950 text-white py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_center,rgba(197,168,128,0.15),transparent_70%)] pointer-events-none" />
        <div className="mx-auto max-w-5xl px-6 text-center relative z-10 flex flex-col items-center gap-6">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-normal font-display tracking-tight text-white leading-tight">
            Klar til en dialog?
          </h2>
          <p className="text-slate-300 max-w-2xl leading-relaxed text-base">
            Vi går altid i dialog med seriøse investorer, der deler vores langsigtede perspektiv. Er du interesseret i infrastruktur og energi som aktivklasse — og søger du stærke projekter med et naturligt interessesammenfald? Så vil vi gerne høre fra dig.
          </p>
          <Link
            to="/kontakt"
            className="mt-8 px-10 py-4 bg-gold-500 text-white text-xs font-bold uppercase tracking-[0.2em] rounded-none hover:bg-white hover:text-navy-950 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gold-400"
          >
            Send en besked
          </Link>
        </div>
      </section>
    </div>
  );
}
